(function () {
  'use strict';
  /* sap-reviews.js v3 — read-more fix + progressive load-more */

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var starAnimationObserver = null;
  var starAnimationTargets = new Set();

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function parseIntSafe(val, fallback) {
    var n = parseInt(val, 10);
    return isNaN(n) ? fallback : n;
  }

  function parseEmbeddedCatalog(section) {
    var el = qs('[data-reviews-catalog]', section);
    if (el) {
      try {
        var inline = JSON.parse(el.textContent || '[]');
        if (Array.isArray(inline) && inline.length) return inline;
      } catch (e) { /* fall through */ }
    }
    return null;
  }

  function fetchEmbeddedCatalog(section) {
    var inline = parseEmbeddedCatalog(section);
    if (inline) return Promise.resolve(inline);
    var url = section.getAttribute('data-reviews-catalog-url') || '';
    if (url.indexOf('//') === 0) url = window.location.protocol + url;
    if (!url) return Promise.resolve([]);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('catalog asset http ' + res.status);
        return res.json();
      })
      .then(function (data) {
        return Array.isArray(data) ? data : [];
      })
      .catch(function () {
        return [];
      });
  }

  function resolveStorefrontToken(section) {
    var token = section.getAttribute('data-storefront-token') || '';
    if (token) return token;
    var features = document.getElementById('shopify-features');
    if (!features) return '';
    try {
      return JSON.parse(features.textContent || '{}').accessToken || '';
    } catch (e) {
      return '';
    }
  }

  function createState(section) {
    var totalCount = parseIntSafe(section.getAttribute('data-count'), 0);
    var ssrCards = qsa('[data-review-card]', section);
    var ssrRendered = ssrCards.length;
    section.setAttribute('data-ssr-rendered', String(ssrRendered));
    var productHandle = section.getAttribute('data-product-handle') || '';
    var catalogUrl = section.getAttribute('data-reviews-catalog-url') || '';
    var inlineCatalog = parseEmbeddedCatalog(section);
    var useEmbedded = !!(catalogUrl || (inlineCatalog && inlineCatalog.length)) && totalCount > ssrRendered;
    var sfToken = resolveStorefrontToken(section);
    var useStorefront = !useEmbedded && !!(sfToken && productHandle && totalCount > ssrRendered);
    return {
      section: section,
      initialCount: parseIntSafe(section.getAttribute('data-initial-count'), 3),
      increment: parseIntSafe(section.getAttribute('data-load-increment'), 9),
      visibleLimit: parseIntSafe(section.getAttribute('data-initial-count'), 3),
      sort: section.getAttribute('data-default-sort') || 'recent',
      ratingFilter: 'all',
      countryFilter: 'all',
      cards: qsa('[data-review-card]', section),
      previousVisible: 0,
      catalog: [],
      catalogReady: false,
      catalogPromise: null,
      cardCache: {},
      embeddedCatalog: inlineCatalog || [],
      catalogUrl: catalogUrl,
      useEmbedded: useEmbedded,
      useStorefront: useStorefront,
      useFullCatalog: useEmbedded || useStorefront,
      gridSyncedWithCatalog: false,
      truncateLength: parseIntSafe(section.getAttribute('data-truncate-length'), 160),
      productHandle: productHandle,
      ratingCounts: (function () {
        try { return JSON.parse(section.getAttribute('data-rating-counts') || '{}'); } catch (e) { return {}; }
      })(),
      planLabels: (function () {
        try { return JSON.parse(section.getAttribute('data-plan-labels') || '{}'); } catch (e) { return {}; }
      })(),
      cardLabels: {
        verified: section.getAttribute('data-card-verified') || 'Verified purchase',
        readMore: section.getAttribute('data-card-read-more') || 'Read more',
        planLabel: section.getAttribute('data-card-plan-label') || 'Access plan',
        featured: section.getAttribute('data-card-featured-label') || 'Featured review'
      }
    };
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function ratingBucketFromValue(rating) {
    var r = parseFloat(rating) || 0;
    if (r >= 4.5) return 5;
    if (r >= 3.5) return 4;
    if (r >= 2.5) return 3;
    if (r >= 1.5) return 2;
    return 1;
  }

  function fieldsToMap(fields) {
    var map = {};
    (fields || []).forEach(function (f) {
      if (f && f.key) map[f.key] = f.value;
    });
    return map;
  }

  function parseReviewNode(node) {
    var f = fieldsToMap(node.fields);
    var rating = parseFloat(f.rating) || 5;
    var cc = (f.country_code || '').trim().toUpperCase();
    return {
      handle: node.handle,
      name: f.reviewer_name || 'Customer',
      rating: rating,
      bucket: ratingBucketFromValue(rating),
      title: f.review_title || '',
      body: f.review_text || '',
      date: f.review_date || '',
      country: f.country || '',
      countryCode: cc,
      language: (f.language || '').trim().toLowerCase(),
      verified: f.purchase_verified === 'true' || f.purchase_verified === true,
      featured: f.featured === 'true' || f.featured === true,
      pinned: f.featured === 'true' || f.featured === true,
      productHandle: (f.product_handle || '').trim()
    };
  }

  function storefrontConfig(section) {
    return {
      domain: section.getAttribute('data-storefront-domain') || '',
      token: resolveStorefrontToken(section),
      version: section.getAttribute('data-storefront-api-version') || '2024-10'
    };
  }

  function storefrontFetch(section, query, variables) {
    var cfg = storefrontConfig(section);
    if (!cfg.domain || !cfg.token) return Promise.reject(new Error('missing storefront config'));
    var url = 'https://' + cfg.domain + '/api/' + cfg.version + '/graphql.json';
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function (res) {
      if (!res.ok) throw new Error('storefront http ' + res.status);
      return res.json();
    }).then(function (payload) {
      if (payload.errors && payload.errors.length) {
        throw new Error(payload.errors[0].message || 'storefront graphql error');
      }
      return payload.data;
    });
  }

  function fetchProductReviewCatalog(state) {
    var section = state.section;
    var handle = state.productHandle;
    var queryWithFilter = 'query Reviews($type:String!,$first:Int!,$after:String,$filters:[MetaobjectFieldFilter!]){metaobjects(type:$type,first:$first,after:$after,filters:$filters){nodes{handle fields{key value}} pageInfo{hasNextPage endCursor}}}';
    var queryAll = 'query Reviews($type:String!,$first:Int!,$after:String){metaobjects(type:$type,first:$first,after:$after){nodes{handle fields{key value}} pageInfo{hasNextPage endCursor}}}';

    function page(vars, useFilter) {
      return storefrontFetch(section, useFilter ? queryWithFilter : queryAll, vars).then(function (data) {
        return data.metaobjects;
      });
    }

    var all = [];
    var cursor = null;
    var useFilter = true;

    function nextPage() {
      var vars = { type: 'review', first: 250, after: cursor };
      if (useFilter) vars.filters = [{ key: 'product_handle', value: handle }];
      return page(vars, useFilter).catch(function () {
        if (!useFilter) throw new Error('catalog fetch failed');
        useFilter = false;
        return page(vars, false);
      }).then(function (conn) {
        var nodes = conn.nodes || [];
        nodes.forEach(function (node) {
          var review = parseReviewNode(node);
          if (!useFilter && review.productHandle !== handle) return;
          all.push(review);
        });
        if (conn.pageInfo && conn.pageInfo.hasNextPage) {
          cursor = conn.pageInfo.endCursor;
          return nextPage();
        }
      });
    }

    return nextPage().then(function () {
      all.sort(function (a, b) {
            return (b.date || '').localeCompare(a.date || '');
      });
      return all;
    });
  }

  function ensureCatalog(state) {
    if (state.catalogReady) return Promise.resolve(state.catalog);
    if (state.catalogPromise) return state.catalogPromise;

    if (state.useEmbedded) {
      state.section.classList.add('sap-reviews--catalog-loading');
      state.catalogPromise = fetchEmbeddedCatalog(state.section).then(function (catalog) {
        state.catalog = catalog;
        state.catalogReady = catalog.length > 0;
        state.section.classList.remove('sap-reviews--catalog-loading');
        if (!state.catalogReady) state.useEmbedded = false;
        return catalog;
      });
      return state.catalogPromise;
    }

    if (!state.useStorefront) return Promise.resolve([]);

    state.section.classList.add('sap-reviews--catalog-loading');
    state.catalogPromise = fetchProductReviewCatalog(state).then(function (catalog) {
      state.catalog = catalog;
      state.catalogReady = true;
      state.section.classList.remove('sap-reviews--catalog-loading');
      return catalog;
    }).catch(function () {
      state.section.classList.remove('sap-reviews--catalog-loading');
      state.useStorefront = false;
      state.catalogPromise = null;
      return [];
    });
    return state.catalogPromise;
  }

  function reviewMatchesFilters(review, state) {
    if (state.ratingFilter && state.ratingFilter !== 'all') {
      var level = parseInt(state.ratingFilter, 10);
      var rating = review.rating;
      if (level === 5 && rating < 4.5) return false;
      if (level === 4 && (rating < 3.5 || rating >= 4.5)) return false;
      if (level === 3 && (rating < 2.5 || rating >= 3.5)) return false;
      if (level === 2 && (rating < 1.5 || rating >= 2.5)) return false;
      if (level === 1 && rating >= 1.5) return false;
    }
    if (state.countryFilter && state.countryFilter !== 'all') {
      if ((review.countryCode || '').toUpperCase() !== state.countryFilter.toUpperCase()) return false;
    }
    return true;
  }

  function compareReviews(a, b, sortKey) {
    if (sortKey === 'highest') return b.rating - a.rating;
    if (sortKey === 'lowest') return a.rating - b.rating;
    return (b.date || '').localeCompare(a.date || '');
  }

  function getFilteredCatalog(state) {
    return state.catalog.filter(function (r) {
      return reviewMatchesFilters(r, state);
    }).slice().sort(function (a, b) {
      return compareReviews(a, b, state.sort);
    });
  }

  function formatReviewDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso + 'T12:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return iso;
    }
  }

  function buildStarHtml(rating) {
    var rounded = Math.round(rating);
    var stars = '';
    for (var i = 0; i < 5; i++) {
      stars += '<span class="sap-star-rating__star' + (i < rounded ? ' sap-star-rating__star--filled' : '') + '" aria-hidden="true">★</span>';
    }
    return '<div class="sap-star-rating sap-star-rating--sm sap-star-rating--animate" role="img" aria-label="Rated ' + rating + ' out of 5"><span class="sap-star-rating__stars">' + stars + '</span></div>';
  }

  function buildReviewCardElement(review, state, index) {
    if (state.cardCache[review.handle]) return state.cardCache[review.handle].cloneNode(true);

    var labels = state.cardLabels;
    var truncate = state.truncateLength;
    var body = review.body || '';
    var needsTruncate = truncate > 0 && body.length > truncate;
    var shortBody = needsTruncate ? body.slice(0, truncate).trim() + '…' : body;
    var initials = (review.name || 'CU').slice(0, 2).toUpperCase();
    var textDir = (review.language === 'ar' || review.language === 'he' || review.language === 'fa' || review.language === 'ur') ? 'rtl' : 'ltr';
    var dirAttr = textDir === 'rtl' ? ' dir="rtl"' : '';
    var planTitle = state.planLabels[review.productHandle] || review.productHandle.replace(/-/g, ' ');
    if (planTitle.indexOf('(3+3') !== -1) planTitle = planTitle.split(' (')[0];
    if (planTitle.indexOf('— 3 Months') !== -1) planTitle = planTitle.replace(' — 3 Months', '');

    var flagHtml = review.countryCode
      ? '<span class="sap-review-card__flag" aria-hidden="true" data-country-flag="' + escapeHtml(review.countryCode) + '"></span>'
      : '';

    var article = document.createElement('article');
    article.className = 'sap-review-card glass sap-review-card--enhanced' +
      (review.featured ? ' sap-review-card--featured sap-review-card--pinned' : '');
    article.setAttribute('data-review-card', '');
    article.setAttribute('data-metaobject-card', '1');
    article.setAttribute('data-rating', String(review.rating));
    article.setAttribute('data-rating-bucket', String(review.bucket));
    article.setAttribute('data-filter-bucket', String(review.bucket));
    article.setAttribute('data-country', review.countryCode || '');
    article.setAttribute('data-date', review.date || '');
    article.setAttribute('data-index', String(index));
    article.setAttribute('data-pinned', review.pinned ? '1' : '0');
    article.setAttribute('data-visible', 'false');
    article.hidden = true;
    if (review.handle) article.id = review.handle;

    var bodyHtml = needsTruncate
      ? '<span class="sap-review-card__text-short">' + escapeHtml(shortBody) + '</span><span class="sap-review-card__text-full" hidden>' + escapeHtml(body) + '</span>'
      : escapeHtml(body);
    var readMoreBtn = needsTruncate
      ? '<button type="button" class="sap-review-card__read-more" data-read-more aria-expanded="false">' + escapeHtml(labels.readMore) + '</button>'
      : '';

    article.innerHTML =
      (review.featured ? '<span class="sap-review-card__featured-label">' + escapeHtml(labels.featured) + '</span>' : '') +
      '<div class="sap-review-card__head">' +
        '<div class="sap-review-card__product">' +
          '<span class="sap-review-card__product-label">' + escapeHtml(labels.planLabel) + '</span>' +
          '<span class="sap-review-card__product-name">' + escapeHtml(planTitle) + '</span>' +
        '</div>' +
        '<div class="sap-review-card__rating-row">' + buildStarHtml(review.rating) +
          '<span class="sap-review-card__rating-value">' + review.rating.toFixed(1) + '/5</span></div>' +
        (review.title ? '<h3 class="sap-review-card__title"' + dirAttr + '>' + escapeHtml(review.title) + '</h3>' : '') +
      '</div>' +
      '<div class="sap-review-card__body"><p class="sap-review-card__text" data-review-text' + dirAttr + '>' + bodyHtml + '</p>' + readMoreBtn + '</div>' +
      '<footer class="sap-review-card__footer sap-review-card__footer--panel">' +
        '<div class="sap-review-card__info-panel">' +
          '<div class="sap-review-card__author">' +
            '<div class="sap-review-card__avatar" aria-hidden="true"><span class="sap-review-card__initials">' + escapeHtml(initials) + '</span></div>' +
            '<div class="sap-review-card__meta">' +
              '<span class="sap-review-card__name">' + escapeHtml(review.name) + '</span>' +
              '<div class="sap-review-card__meta-row">' +
                (review.country ? '<span class="sap-review-card__location">' + flagHtml + escapeHtml(review.country) + '</span>' : '') +
                (review.language ? '<span class="sap-review-card__lang">' + escapeHtml(review.language.toUpperCase()) + '</span>' : '') +
                (review.date ? '<time class="sap-review-card__date" datetime="' + escapeHtml(review.date) + '">' + escapeHtml(formatReviewDate(review.date)) + '</time>' : '') +
              '</div>' +
            '</div>' +
          '</div>' +
          '' +
        '</div>' +
      '</footer>';

    state.cardCache[review.handle] = article;
    return article.cloneNode(true);
  }

  function hydrateReadMoreButton(btn, section) {
    if (!btn) return;
    var readLessLabel = (section && section.getAttribute('data-read-less')) || 'Show less';
    if (!btn.getAttribute('data-read-more-label')) {
      btn.setAttribute('data-read-more-label', btn.textContent.trim());
    }
    if (!btn.getAttribute('data-read-less-label')) {
      btn.setAttribute('data-read-less-label', readLessLabel);
    }
  }

  function toggleReviewReadMore(btn, section) {
    hydrateReadMoreButton(btn, section);
    var readLessLabel = btn.getAttribute('data-read-less-label') || 'Show less';
    var card = btn.closest('[data-review-card]');
    if (!card) return false;

    var expanded = btn.getAttribute('aria-expanded') === 'true';
    var nextExpanded = !expanded;
    var shortEl = qs('.sap-review-card__text-short', card);
    var fullEl = qs('.sap-review-card__text-full', card);

    if (shortEl && fullEl) {
      shortEl.hidden = nextExpanded;
      fullEl.hidden = !nextExpanded;
    } else {
      return false;
    }

    btn.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
    btn.textContent = nextExpanded
      ? readLessLabel
      : (btn.getAttribute('data-read-more-label') || 'Read more');
    card.classList.toggle('is-read-expanded', nextExpanded);
    return true;
  }

  function snapshotExpandedReviewCards(cards) {
    var expanded = {};
    cards.forEach(function (card) {
      var btn = qs('[data-read-more][aria-expanded="true"]', card);
      if (!btn) return;
      var key = card.getAttribute('data-index') || card.getAttribute('data-date') || '';
      if (key) expanded[key] = true;
    });
    return expanded;
  }

  function restoreExpandedReviewCards(cards, expandedMap) {
    if (!expandedMap || !Object.keys(expandedMap).length) return;
    cards.forEach(function (card) {
      var key = card.getAttribute('data-index') || card.getAttribute('data-date') || '';
      if (!key || !expandedMap[key]) return;
      var btn = qs('[data-read-more]', card);
      if (btn && btn.getAttribute('aria-expanded') !== 'true') {
        toggleReviewReadMore(btn, card.closest('[data-sap-reviews]') || card.closest('[data-home-reviews]'));
      }
    });
  }

  function rebuildGridFromCatalog(state) {
    var grid = qs('[data-reviews-grid]', state.section);
    if (!grid) return;

    var scrollY = window.scrollY;
    var expandedMap = snapshotExpandedReviewCards(state.cards);
    var filtered = getFilteredCatalog(state);

    cleanupStarAnimations(grid);
    grid.innerHTML = '';
    state.cards = [];
    filtered.forEach(function (review, index) {
      var card = buildReviewCardElement(review, state, index);
      grid.appendChild(card);
      state.cards.push(card);
    });
    state.gridSyncedWithCatalog = true;

    hydrateCountryFlags(state.section);
    qsa('[data-read-more]', state.section).forEach(function (btn) {
      hydrateReadMoreButton(btn, state.section);
    });
    restoreExpandedReviewCards(state.cards, expandedMap);
    initStarAnimations(state.section);

    requestAnimationFrame(function () {
      window.scrollTo({ top: scrollY, behavior: 'auto' });
    });
  }

  function loadStorefrontView(state) {
    var grid = qs('[data-reviews-grid]', state.section);
    if (grid) grid.classList.add('is-loading');
    return ensureCatalog(state).then(function () {
      rebuildGridFromCatalog(state);
      if (grid) grid.classList.remove('is-loading');
      applyViewState(state);
    });
  }

  function prefetchCatalog(state) {
    if (!state.useFullCatalog) return;
    var grid = qs('[data-reviews-grid]', state.section);
    ensureCatalog(state).then(function () {
      if (!state.catalogReady || state.section.isConnected === false) return;
      var active = document.activeElement;
      if (active && state.section.contains(active) && active.closest('[data-review-card]')) return;
      if (state.cards.length < getFilteredCatalog(state).length) {
        rebuildGridFromCatalog(state);
        applyViewState(state);
      }
      if (grid) grid.classList.remove('is-loading');
    });
  }

  function scheduleCatalogPrefetch(state) {
    if (!state.useFullCatalog) return;
    var section = state.section;
    var waiting = true;
    var observer = null;

    function targetsSection(hash) {
      if (!hash || hash === '#') return false;
      var id;
      try { id = decodeURIComponent(hash.slice(1)); } catch (error) { return false; }
      var target = document.getElementById(id);
      return !!target && (target === section || section.contains(target));
    }

    function cleanup() {
      waiting = false;
      if (observer) observer.disconnect();
      section.removeEventListener('click', start, true);
      section.removeEventListener('focusin', start, true);
      section.removeEventListener('change', start, true);
      document.removeEventListener('click', onAnchor, true);
      document.removeEventListener('shopify:section:unload', onUnload);
      window.removeEventListener('hashchange', onHash);
    }

    function start() {
      if (!waiting) return;
      cleanup();
      if (section.isConnected === false) return;
      prefetchCatalog(state);
    }

    function onHash() {
      if (targetsSection(window.location.hash)) start();
    }

    function onAnchor(event) {
      var link = event.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) === '#' && targetsSection(href)) start();
    }

    function onUnload(event) {
      if (event.target === section || event.target.contains(section)) cleanup();
    }

    if (!('IntersectionObserver' in window) || targetsSection(window.location.hash)) {
      start();
      return;
    }

    observer = new IntersectionObserver(function (entries) {
      if (entries.some(function (entry) { return entry.isIntersecting; })) start();
    }, { rootMargin: '600px 0px', threshold: 0 });
    section.addEventListener('click', start, true);
    section.addEventListener('focusin', start, true);
    section.addEventListener('change', start, true);
    document.addEventListener('click', onAnchor, true);
    document.addEventListener('shopify:section:unload', onUnload);
    window.addEventListener('hashchange', onHash);
    observer.observe(section);
  }

  function syncHeroFromReviews(section) {
    var hero = qs('[data-reviews-hero]');
    if (!section || !hero) return;
    if (hero.getAttribute('data-hero-pending') !== 'true') return;

    var avg = section.getAttribute('data-average');
    var count = section.getAttribute('data-count');
    if (avg) {
      hero.setAttribute('data-average', avg);
      var scoreEl = qs('.sap-reviews-hero-rating__score', hero);
      if (scoreEl) scoreEl.textContent = avg;
      var starsWrap = qs('.sap-star-rating', hero);
      if (starsWrap) starsWrap.setAttribute('aria-label', 'Rated ' + avg + ' out of 5 stars');
    }
    if (count) {
      hero.setAttribute('data-count', count);
      var countEl = qs('.sap-reviews-hero-rating__count', hero);
      if (countEl) {
        var tpl = hero.getAttribute('data-count-label');
        countEl.textContent = tpl ? tpl.replace('__COUNT__', count) : count + ' reviews';
      }
    }
    hero.classList.remove('sap-reviews-hero-rating--pending');
    hero.removeAttribute('data-hero-pending');
  }

  function starRatingMatches(card, filter) {
    if (!filter || filter === 'all') return true;
    var rating = parseFloat(card.getAttribute('data-rating')) || 0;
    var level = parseInt(filter, 10);
    if (level === 5) return rating >= 4.5;
    if (level === 4) return rating >= 3.5 && rating < 4.5;
    if (level === 3) return rating >= 2.5 && rating < 3.5;
    if (level === 2) return rating >= 1.5 && rating < 2.5;
    if (level === 1) return rating < 1.5;
    return String(card.getAttribute('data-rating-bucket')) === String(filter);
  }

  function countryMatches(card, filter) {
    if (!filter || filter === 'all') return true;
    return (card.getAttribute('data-country') || '').toUpperCase() === filter.toUpperCase();
  }

  function cardMatchesFilters(card, state) {
    return starRatingMatches(card, state.ratingFilter) && countryMatches(card, state.countryFilter);
  }

  function compareCards(a, b, sortKey) {
    var ratingA = parseFloat(a.getAttribute('data-rating')) || 0;
    var ratingB = parseFloat(b.getAttribute('data-rating')) || 0;
    var dateA = a.getAttribute('data-date') || '';
    var dateB = b.getAttribute('data-date') || '';

    if (sortKey === 'highest') return ratingB - ratingA;
    if (sortKey === 'lowest') return ratingA - ratingB;
    return dateB.localeCompare(dateA);
  }

  function sortCardsInDom(state) {
    var grid = qs('[data-reviews-grid]', state.section);
    if (!grid) return;

    var sorted = state.cards.slice().sort(function (a, b) {
      return compareCards(a, b, state.sort);
    });

    var current = qsa('[data-review-card]', grid);
    var alreadySorted = current.length === sorted.length && sorted.every(function (card, index) {
      return current[index] === card && card.parentNode === grid;
    });
    if (alreadySorted) return;

    sorted.forEach(function (card) {
      grid.appendChild(card);
    });
  }

  function getFilteredCards(state) {
    return state.cards.filter(function (card) {
      return cardMatchesFilters(card, state);
    });
  }

  function getSortedFilteredCards(state) {
    return getFilteredCards(state).slice().sort(function (a, b) {
      return compareCards(a, b, state.sort);
    });
  }

  function getMatchTotal(state) {
    if (state.useFullCatalog && state.catalogReady && state.gridSyncedWithCatalog) {
      return getFilteredCatalog(state).length;
    }
    if (state.ratingFilter !== 'all' && state.ratingCounts[state.ratingFilter]) {
      return parseIntSafe(state.ratingCounts[state.ratingFilter], getFilteredCards(state).length);
    }
    return getFilteredCards(state).length;
  }

  function applyFilterChange(state) {
    resetVisibleLimit(state);
    state.previousVisible = 0;
    if (state.useFullCatalog) {
      loadStorefrontView(state);
    } else {
      applyViewState(state);
    }
  }
  function resetVisibleLimit(state) {
    state.visibleLimit = state.initialCount;
    state.section.setAttribute('data-shown-limit', String(state.visibleLimit));
  }

  function setStarFilter(state, filterVal) {
    state.ratingFilter = filterVal || 'all';
    state.section.setAttribute('data-star-filter', state.ratingFilter);
    qsa('.sap-reviews__filter[data-star-filter]', state.section).forEach(function (btn) {
      var active = btn.getAttribute('data-star-filter') === state.ratingFilter;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function updateStatusUI(state, filtered, visibleCount) {
    var note = qs('[data-showing-count]', state.section);
    var total = state.section.getAttribute('data-count') || String(filtered.length);
    var hasFilter = state.ratingFilter !== 'all' || state.countryFilter !== 'all';
    var matchTotal = hasFilter ? getMatchTotal(state) : parseIntSafe(total, filtered.length);

    if (note) {
      var tpl = hasFilter
        ? state.section.getAttribute('data-showing-filtered-template') || ''
        : state.section.getAttribute('data-showing-template') || '';
      note.textContent = tpl
        .replace(/__SHOWN__/g, String(visibleCount))
        .replace(/__MATCH__/g, String(matchTotal))
        .replace(/__VISIBLE__/g, String(visibleCount))
        .replace(/__TOTAL__/g, total);
      note.classList.add('is-updated');
      window.setTimeout(function () {
        note.classList.remove('is-updated');
      }, 400);
    }

    var emptyEl = qs('[data-reviews-empty-filter]', state.section);
    if (emptyEl) {
      emptyEl.hidden = !(hasFilter && filtered.length === 0);
    }
  }

  function updateLoadMoreButton(state, filtered, visibleCount) {
    var btn = qs('[data-load-more]', state.section);
    if (!btn) return;

    var remaining = Math.max(0, filtered.length - visibleCount);
    if (remaining <= 0 || visibleCount >= filtered.length) {
      btn.hidden = true;
      btn.classList.add('is-hidden');
      btn.setAttribute('aria-hidden', 'true');
      btn.classList.remove('is-loading');
      return;
    }

    btn.hidden = false;
    btn.classList.remove('is-hidden');
    btn.removeAttribute('aria-hidden');
    var nextBatch = Math.min(state.increment, remaining);
    var textEl = qs('[data-load-more-text]', btn);
    if (!textEl) return;

    var tpl =
      state.visibleLimit <= state.initialCount
        ? state.section.getAttribute('data-load-more-first') || ''
        : state.section.getAttribute('data-load-more-more') || '';
    textEl.textContent = tpl.replace(/__COUNT__/g, String(nextBatch));
  }

  function revealCard(card, animate, pendingReveals) {
    card.hidden = false;
    card.classList.add('is-shown');
    card.classList.remove('sap-review-card--filtered-out');

    if (animate && !prefersReducedMotion) {
      card.classList.remove('is-revealed');
      pendingReveals.push(card);
    } else {
      card.classList.add('is-revealed');
    }
  }

  function hideCard(card) {
    card.hidden = true;
    card.classList.remove('is-shown', 'is-revealed');
  }

  function render(state) {
    var expandedMap = snapshotExpandedReviewCards(state.cards);
    state.cards = qsa('[data-review-card]', state.section);
    sortCardsInDom(state);
    var filtered = getSortedFilteredCards(state);
    var prevVisible = state.previousVisible;
    var visibleCount = 0;
    var pendingReveals = [];

    state.cards.forEach(function (card) {
      hideCard(card);
      card.classList.add('sap-review-card--filtered-out');
      card.setAttribute('data-visible', 'false');
    });

    filtered.forEach(function (card, index) {
      card.classList.remove('sap-review-card--filtered-out');
      card.setAttribute('data-visible', 'true');
      if (index < state.visibleLimit) {
        revealCard(card, index >= prevVisible, pendingReveals);
        visibleCount++;
      }
    });

    if (pendingReveals.length) {
      var grid = qs('[data-reviews-grid]', state.section);
      void (grid || state.section).offsetWidth;
      pendingReveals.forEach(function (card) { card.classList.add('is-revealed'); });
    }

    restoreExpandedReviewCards(state.cards, expandedMap);
    state.previousVisible = visibleCount;
    updateStatusUI(state, filtered, visibleCount);
    updateLoadMoreButton(state, filtered, visibleCount);
    state.section.setAttribute('data-shown-limit', String(state.visibleLimit));
  }

  function applyViewState(state) {
    render(state);
  }

  function animateBars(root) {
    qsa('.sap-reviews-summary__bar-fill', root).forEach(function (bar) {
      var pct = bar.getAttribute('data-bar-pct') || '0';
      if (prefersReducedMotion) {
        bar.style.width = pct + '%';
        return;
      }
      requestAnimationFrame(function () {
        bar.style.width = pct + '%';
      });
    });
  }

  function cleanupStarAnimations(root) {
    if (!root || typeof root.contains !== 'function') return;
    starAnimationTargets.forEach(function (group) {
      if (group !== root && !root.contains(group)) return;
      if (starAnimationObserver) starAnimationObserver.unobserve(group);
      starAnimationTargets.delete(group);
    });
    if (!starAnimationTargets.size && starAnimationObserver) {
      starAnimationObserver.disconnect();
      starAnimationObserver = null;
    }
  }

  function initStarAnimations(root) {
    if (prefersReducedMotion) return;

    qsa('.sap-star-rating--animate', root).forEach(function (group) {
      if (group.getAttribute('data-star-animated') === '1' || starAnimationTargets.has(group)) return;
      if (!('IntersectionObserver' in window)) {
        group.classList.add('sap-star-rating--visible');
        return;
      }

      if (!starAnimationObserver) {
        var observer = new IntersectionObserver(
          function (entries) {
            if (starAnimationObserver !== observer) return;
            entries.forEach(function (entry) {
              if (!entry.isIntersecting || !starAnimationTargets.has(entry.target)) return;
              entry.target.classList.add('sap-star-rating--visible');
              entry.target.setAttribute('data-star-animated', '1');
              observer.unobserve(entry.target);
              starAnimationTargets.delete(entry.target);
            });
            if (!starAnimationTargets.size) {
              observer.disconnect();
              starAnimationObserver = null;
            }
          },
          { threshold: 0.1 }
        );
        starAnimationObserver = observer;
      }
      starAnimationTargets.add(group);
      starAnimationObserver.observe(group);
    });
  }

  function initInteractions(state) {
    var section = state.section;

    section.addEventListener('click', function (e) {
      var loadBtn = e.target.closest('[data-load-more]');
      if (loadBtn && section.contains(loadBtn)) {
        e.preventDefault();
        var filtered = getSortedFilteredCards(state);
        var prevLimit = state.visibleLimit;
        var nextLimit = Math.min(state.visibleLimit + state.increment, filtered.length);

        if (state.useFullCatalog && !state.catalogReady) {
          loadBtn.classList.add('is-loading');
          ensureCatalog(state).then(function () {
            rebuildGridFromCatalog(state);
            filtered = getSortedFilteredCards(state);
            nextLimit = Math.min(state.visibleLimit + state.increment, filtered.length);
            state.visibleLimit = nextLimit;
            state.previousVisible = prevLimit;
            applyViewState(state);
            loadBtn.classList.remove('is-loading');
          });
          return;
        }

        if (state.useFullCatalog && state.catalogReady && state.cards.length < getFilteredCatalog(state).length) {
          rebuildGridFromCatalog(state);
          filtered = getSortedFilteredCards(state);
          nextLimit = Math.min(state.visibleLimit + state.increment, filtered.length);
        }

        var ssrRendered = parseIntSafe(section.getAttribute('data-ssr-rendered'), state.cards.length);
        var needsFetch = !state.useFullCatalog && nextLimit > ssrRendered && state.cards.length < filtered.length;

        loadBtn.classList.add('is-loading');

        function finishLoad() {
          state.visibleLimit = nextLimit;
          state.previousVisible = prevLimit;
          applyViewState(state);
          loadBtn.classList.remove('is-loading');
          initStarAnimations(section);
          if (filtered[prevLimit] && filtered[prevLimit].scrollIntoView) {
            filtered[prevLimit].scrollIntoView({
              behavior: prefersReducedMotion ? 'auto' : 'smooth',
              block: 'nearest'
            });
          }
        }

        if (needsFetch) {
          fetchReviewBatch(state, state.cards.length, state.increment).then(function (ok) {
            if (ok) {
              section.setAttribute('data-ssr-rendered', String(state.cards.length));
              filtered = getSortedFilteredCards(state);
              nextLimit = Math.min(state.visibleLimit + state.increment, filtered.length);
            }
            finishLoad();
          });
        } else {
          finishLoad();
        }
        return;
      }

      var starBtn = e.target.closest('[data-star-filter]');
      if (starBtn && section.contains(starBtn)) {
        e.preventDefault();
        setStarFilter(state, starBtn.getAttribute('data-star-filter'));
        applyFilterChange(state);
        return;
      }

      var readBtn = e.target.closest('[data-read-more]');
      if (readBtn && section.contains(readBtn)) {
        return;
      }
    });

    var countrySelect = qs('[data-country-filter]', section);
    if (countrySelect) {
      countrySelect.addEventListener('change', function () {
        state.countryFilter = countrySelect.value || 'all';
        section.setAttribute('data-country-filter', state.countryFilter);
        applyFilterChange(state);
      });
    }

    var sortSelect = qs('[data-reviews-sort]', section);
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        state.sort = sortSelect.value;
        applyFilterChange(state);
      });
    }
  }

  function initStarPicker(form) {
    var picker = qs('[data-star-picker]', form);
    if (!picker) return;

    var section = getFormContext(form);
    var labelEl = qs('[data-rating-label]', picker);
    var ratingLabels = [];
    if (section) {
      ratingLabels = (section.getAttribute('data-rating-labels') || '').split('|');
    }

    var options = qsa('.sap-reviews__star-option', picker);

    function updateRatingLabel(value) {
      if (!labelEl || !value) return;
      var text = ratingLabels[value - 1] || value + ' stars';
      labelEl.textContent = text;
      labelEl.classList.add('is-visible');
    }

    function highlightStars(options, upTo, selectedValue) {
      options.forEach(function (opt, i) {
        var input = qs('input[name="review_rating"]', opt);
        var isFilled = i < upTo;
        opt.classList.toggle('is-active', isFilled);
        opt.classList.toggle('is-selected', selectedValue && input && parseInt(input.value, 10) === selectedValue);
      });
    }

    options.forEach(function (opt, idx) {
      opt.addEventListener('mouseenter', function () {
        highlightStars(options, idx + 1, null);
      });
      opt.addEventListener('focusin', function () {
        highlightStars(options, idx + 1, null);
        updateRatingLabel(idx + 1);
      });
    });

    picker.addEventListener('mouseleave', function () {
      var checked = qs('input[name="review_rating"]:checked', picker);
      var val = checked ? parseInt(checked.value, 10) : 0;
      highlightStars(options, val, val);
      updateRatingLabel(val);
    });

    var ratingInputs = qsa('input[name="review_rating"]', picker);
    ratingInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var val = parseInt(input.value, 10);
        highlightStars(options, val, val);
        updateRatingLabel(val);
      });
      input.addEventListener('keydown', function (event) {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        var enabledInputs = ratingInputs.filter(function (radio) {
          return !radio.disabled && !radio.matches(':disabled');
        });
        var index = enabledInputs.indexOf(input);
        if (index < 0) return;
        var nextIndex;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % enabledInputs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + enabledInputs.length) % enabledInputs.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = enabledInputs.length - 1;
        else return;
        event.preventDefault();
        // Keep focus, the native checked value and existing feedback in sync.
        var nextInput = enabledInputs[nextIndex];
        nextInput.focus();
        nextInput.checked = true;
        nextInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    var initial = qs('input[name="review_rating"]:checked', picker);
    if (initial) {
      var initVal = parseInt(initial.value, 10);
      highlightStars(options, initVal, initVal);
      updateRatingLabel(initVal);
    }

    options.forEach(function (opt, idx) {
      opt.addEventListener('click', function () {
        var input = qs('input[name="review_rating"]', opt);
        if (!input) return;
        input.checked = true;
        var val = parseInt(input.value, 10);
        highlightStars(options, val, val);
        updateRatingLabel(val);
      });
    });
  }

  function getFormContext(el) {
    if (!el) return null;
    return el.closest('[data-sap-reviews]') || el.closest('[data-sap-review-submit]') || el.closest('section');
  }

  function getCityValue(form) {
    var city = qs('[data-field="city"]', form);
    return city && city.value ? city.value.trim() : '';
  }

  function validateForm(form, section) {
    var errors = [];
    var name = qs('[data-field="name"]', form);
    var title = qs('[data-field="title"]', form);
    var rating = qs('[data-field="rating"]:checked', form);
    var body = qs('[data-field="body"]', form);
    var honeypot = qs('.sap-reviews__honeypot', form);

    var bodyVal = body ? body.value.trim() : '';
    var titleVal = title ? title.value.trim() : '';
    var errTitle = section ? section.getAttribute('data-form-error-title-required') : '';

    if (honeypot && honeypot.value) errors.push('Spam detected.');
    if (!name || name.value.trim().length < 2) errors.push('Please enter your name.');
    if (!title || titleVal.length < 3) errors.push(errTitle || 'Please enter a review title.');
    if (!rating) errors.push('Please select a star rating.');
    if (!body || bodyVal.length < 10) errors.push('Review must be at least 10 characters.');
    return errors;
  }

  function composeReviewBody(form, contextEl, productHandle) {
    var composed = qs('[data-field="body"]', form);
    if (!composed) return;

    var name = (qs('[data-field="name"]', form) || {}).value || '';
    var email = (qs('[data-field="email"]', form) || {}).value || '';
    var rating = (qs('[data-field="rating"]:checked', form) || {}).value || '';
    var title = (qs('[data-field="title"]', form) || {}).value || '';
    var countryEl = qs('[data-field="country"]', form);
    var city = getCityValue(form);
    var country = countryEl && countryEl.value ? countryEl.value.trim() : '';
    var countryLabel = '';
    if (countryEl && countryEl.selectedIndex >= 0) {
      countryLabel = countryEl.options[countryEl.selectedIndex].text.trim();
    }
    var modeWrap = qs('[data-review-mode]', form);
    var mode = modeWrap ? modeWrap.getAttribute('data-active-mode') || 'free' : 'free';
    var templateId = (qs('[data-field="template"]', form) || {}).value || '';
    var reviewText = composed.value.trim();
    var lines = [
      '--- Product Review Submission ---',
      'Product: ' + (productHandle || 'unknown'),
      'Rating: ' + rating + '/5',
      'Title: ' + (title || '(none)')
    ];

    if (city && country) {
      lines.push('Location: ' + city + ', ' + (countryLabel || country));
      lines.push('Location code: ' + city + ', ' + country);
    } else if (country) {
      lines.push('Location: ' + (countryLabel || country));
    }
    if (mode === 'template' && templateId) {
      lines.push('Mode: template (' + templateId + ')');
    } else {
      lines.push('Mode: free');
    }

    lines.push('', reviewText, '', 'From: ' + name + (email ? ' <' + email + '>' : ''));
    composed.value = lines.join('\n');
  }

  function bindReviewForm(form, contextEl) {
    if (!form || form.getAttribute('data-reviews-form-init') === '1') return;
    form.setAttribute('data-reviews-form-init', '1');

    initStarPicker(form);

    var productHandle = contextEl ? contextEl.getAttribute('data-product-handle') || '' : '';
    if (form.id === 'sap-review-submit-form') {
      var productSelect = qs('[data-field="product"]', form);
      if (productSelect) {
        productSelect.addEventListener('change', function () {
          productHandle = productSelect.value || '';
        });
        productHandle = productSelect.value || productHandle;
      }
    }

    form.addEventListener('submit', function (e) {
      var honeypot = qs('.sap-reviews__honeypot', form);
      if (honeypot && honeypot.value) {
        e.preventDefault();
        return;
      }

      var errors = validateForm(form, contextEl);
      if (errors.length) {
        e.preventDefault();
        var errBox = qs('.sap-reviews__form-errors', form) || document.createElement('div');
        errBox.className = 'sap-reviews__form-errors';
        errBox.setAttribute('role', 'alert');
        errBox.textContent = errors.join(' ');
        if (!qs('.sap-reviews__form-errors', form)) {
          form.insertBefore(errBox, form.firstChild);
        }
        return;
      }

      composeReviewBody(form, contextEl, productHandle);
    });

    if (window.location.search.indexOf('contact_posted=true') !== -1) {
      var success = qs('[data-form-success]', contextEl || document);
      if (success) success.hidden = false;
      form.hidden = true;
    }
  }

  function initForm(section) {
    var form = qs('#sap-review-form', section);
    if (!form) return;
    bindReviewForm(form, section);
  }

  function initReviewSubmitPage() {
    var section = qs('[data-sap-review-submit]');
    if (!section || section.getAttribute('data-submit-init') === '1') return;
    section.setAttribute('data-submit-init', '1');
    var form = qs('#sap-review-submit-form', section);
    if (form) bindReviewForm(form, section);
  }

  function fetchReviewBatch(state, start, size) {
    var section = state.section;
    var batchId = section.getAttribute('data-batch-section') || 'sap-reviews-batch';
    var handle = section.getAttribute('data-product-handle') || '';
    if (!handle) return Promise.resolve(false);

    var path = window.location.pathname.indexOf('/products/') === 0
      ? window.location.pathname
      : '/products/' + handle;
    var url = path + '?section_id=' + encodeURIComponent(batchId) +
      '&batch_start=' + encodeURIComponent(String(start)) +
      '&batch_size=' + encodeURIComponent(String(size)) +
      '&product_handle=' + encodeURIComponent(handle);

    return fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('batch fetch failed');
        return response.text();
      })
      .then(function (html) {
        var grid = qs('[data-reviews-grid]', section);
        if (!grid) return false;
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var cards = qsa('[data-review-card]', wrapper);
        if (!cards.length) return false;
        cards.forEach(function (card) {
          grid.appendChild(card);
        });
        state.cards = qsa('[data-review-card]', section);
        hydrateCountryFlags(section);
        qsa('[data-read-more]', section).forEach(function (btn) {
          hydrateReadMoreButton(btn, section);
        });
        initStarAnimations(section);
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  var FLAG_MAP = {
    DE: '\uD83C\uDDE9\uD83C\uDDEA', GB: '\uD83C\uDDEC\uD83C\uDDE7', UK: '\uD83C\uDDEC\uD83C\uDDE7', US: '\uD83C\uDDFA\uD83C\uDDF8',
    FR: '\uD83C\uDDEB\uD83C\uDDF7', NL: '\uD83C\uDDF3\uD83C\uDDF1', CH: '\uD83C\uDDE8\uD83C\uDDED', CA: '\uD83C\uDDE8\uD83C\uDDE6',
    AU: '\uD83C\uDDE6\uD83C\uDDFA', SG: '\uD83C\uDDF8\uD83C\uDDEC', JP: '\uD83C\uDDEF\uD83C\uDDF5', KR: '\uD83C\uDDF0\uD83C\uDDF7',
    IN: '\uD83C\uDDEE\uD83C\uDDF3', ES: '\uD83C\uDDEA\uD83C\uDDF8', SE: '\uD83C\uDDF8\uD83C\uDDEA', AE: '\uD83C\uDDE6\uD83C\uDDEA',
    SA: '\uD83C\uDDF8\uD83C\uDDE6', EG: '\uD83C\uDDEA\uD83C\uDDEC', BR: '\uD83C\uDDE7\uD83C\uDDF7', MX: '\uD83C\uDDF2\uD83C\uDDFD', IT: '\uD83C\uDDEE\uD83C\uDDF9',
    NO: '\uD83C\uDDF3\uD83C\uDDF4', IE: '\uD83C\uDDEE\uD83C\uDDEA', PL: '\uD83C\uDDF5\uD83C\uDDF1', AT: '\uD83C\uDDE6\uD83C\uDDF9'
  };

  function hydrateCountryFlags(root) {
    qsa('[data-country-flag]', root || document).forEach(function (el) {
      var cc = (el.getAttribute('data-country-flag') || '').toUpperCase();
      if (FLAG_MAP[cc]) el.textContent = FLAG_MAP[cc];
    });
  }

  function refreshStaleHomeReviews() {
    var home = qs('.sap-home-reviews');
    if (!home) return;

    var stale = qs('.sap-home-reviews__empty', home) ||
      qs('.sap-reviews-summary--compact', home) ||
      !qs('.sap-home-reviews__score-card', home) ||
      !qs('[data-home-reviews-rail]', home) ||
      !qs('.sap-home-reviews__rail-zone', home) ||
      !qs('.sap-review-card__title', home);

    if (!stale || home.getAttribute('data-refreshing') === '1') return;
    home.setAttribute('data-refreshing', '1');

    var url = window.location.pathname + '?section_id=sap-home-reviews&reviews_refresh=' + Date.now();
    fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('reviews section refresh failed');
        return response.text();
      })
      .then(function (html) {
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var freshHome = qs('.sap-home-reviews', wrapper);
        if (!freshHome || !qs('.sap-home-reviews__score-card', freshHome)) return;

        home.replaceWith(freshHome);
        hydrateCountryFlags(freshHome);
        initHomeReviewsRail(freshHome);
      })
      .catch(function () {
        home.removeAttribute('data-refreshing');
      });
  }

  function initStaticHomeReviewsRail(home) {
    var rail=qs('[data-home-reviews-rail]',home);
    if(!rail||rail.getAttribute('data-rail-init')==='1')return;
    var viewport=qs('.sap-home-reviews__rail-viewport',rail);
    var track=qs('[data-home-reviews-track]',rail);
    if(!viewport||!track)return;
    rail.setAttribute('data-rail-init','1');
    home.setAttribute('data-rail-js','1');
    var previous=qs('[data-home-reviews-control="prev"]',home);
    var next=qs('[data-home-reviews-control="next"]',home);
    viewport.tabIndex=0;
    viewport.setAttribute('role','region');
    viewport.setAttribute('aria-label',rail.getAttribute('aria-label')||'Customer reviews');
    function updateButtons(){
      previous.disabled=viewport.scrollLeft<=2;
      next.disabled=viewport.scrollLeft>=viewport.scrollWidth-viewport.clientWidth-2;
    }
    function resize(){
      var count=window.innerWidth>=1100?3:window.innerWidth>=720?2:1;
      var gap=parseFloat(window.getComputedStyle(track).gap)||18;
      viewport.style.setProperty('--sap-home-reviews-card-width',((viewport.clientWidth-gap*(count-1))/count)+'px');
      viewport.style.setProperty('--sap-home-reviews-visible',String(count));
      updateButtons();
    }
    function move(direction){
      var card=qs('[data-review-card]',rail);
      var gap=parseFloat(window.getComputedStyle(track).gap)||18;
      viewport.scrollBy({left:direction*((card?card.getBoundingClientRect().width:viewport.clientWidth)+gap),behavior:prefersReducedMotion?'auto':'smooth'});
    }
    previous.addEventListener('click',function(){move(-1);});
    next.addEventListener('click',function(){move(1);});
    viewport.addEventListener('scroll',updateButtons,{passive:true});
    viewport.addEventListener('keydown',function(event){
      if(event.target!==viewport)return;
      if(event.key==='ArrowRight'){event.preventDefault();move(1);}
      if(event.key==='ArrowLeft'){event.preventDefault();move(-1);}
      if(event.key==='Home'){event.preventDefault();viewport.scrollTo({left:0,behavior:'auto'});}
      if(event.key==='End'){event.preventDefault();viewport.scrollTo({left:viewport.scrollWidth,behavior:'auto'});}
    });
    qsa('[data-read-more]',home).forEach(function(button){hydrateReadMoreButton(button,home);});
    resize();
    window.addEventListener('resize',resize);
    home._homeReviewsRailCleanup=function(){window.removeEventListener('resize',resize);};
  }

  function initHomeReviewsRail(home) {
    if(home && home.getAttribute('data-marquee-enabled')!=='1'){initStaticHomeReviewsRail(home);return;}
    if (!home) return;

    var rail = qs('[data-home-reviews-rail]', home);
    if (!rail || rail.getAttribute('data-rail-init') === '1') return;

    var viewport = qs('.sap-home-reviews__rail-viewport', rail);
    var track = qs('[data-home-reviews-track]', rail);
    if (!viewport || !track) return;

    rail.setAttribute('data-rail-init', '1');
    home.setAttribute('data-rail-js', '1');

    var marqueeEnabled = home.getAttribute('data-marquee-enabled') === '1' && !prefersReducedMotion;
    var offsetPx = 0;
    var autoSpeed = 0.35;
    var manualUntil = 0;
    var animId = null;
    function syncCardWidth() {
      var visible = window.innerWidth <= 1023 ? 1 : 3;
      var trackStyles = window.getComputedStyle(track);
      var gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '16') || 16;
      var cardW = (viewport.clientWidth - gap * (visible - 1)) / visible;
      viewport.style.setProperty('--sap-home-reviews-card-width', cardW + 'px');
      viewport.style.setProperty('--sap-home-reviews-visible', String(visible));
    }

    syncCardWidth();
    window.addEventListener('resize', syncCardWidth);

    function loopWidth() {
      return track.scrollWidth / 2;
    }

    function applyTransform() {
      track.style.transform = 'translate3d(' + offsetPx + 'px, 0, 0)';
    }

    function normalizeOffset() {
      var half = loopWidth();
      if (!half) return;
      while (offsetPx <= -half) offsetPx += half;
      while (offsetPx > 0) offsetPx -= half;
    }

    function pauseManual(ms) {
      manualUntil = Date.now() + (ms || 2200);
    }

    function cardStep() {
      var card = qs('[data-review-card]', rail);
      if (!card) return Math.max(280, viewport.clientWidth / 3);
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function tick() {
      if (marqueeEnabled && Date.now() >= manualUntil) {
        offsetPx -= autoSpeed;
        normalizeOffset();
      }
      applyTransform();
      animId = requestAnimationFrame(tick);
    }

    function nudge(direction) {
      offsetPx += direction * cardStep();
      normalizeOffset();
      applyTransform();
      pauseManual(2400);
    }

    function onWheel(event) {
      var absX = Math.abs(event.deltaX);
      var absY = Math.abs(event.deltaY);
      if (absY >= absX) return;
      var delta = event.deltaX;
      if (!delta) return;

      event.preventDefault();
      offsetPx -= delta;
      normalizeOffset();
      applyTransform();
      pauseManual(2400);
      rail.classList.add('is-wheeling');
      clearTimeout(rail._wheelClassTimer);
      rail._wheelClassTimer = setTimeout(function () {
        rail.classList.remove('is-wheeling');
      }, 180);
    }

    qsa('[data-home-reviews-control]', home).forEach(function (btn) {
      btn.addEventListener('click', function () {
        nudge(btn.getAttribute('data-home-reviews-control') === 'prev' ? 1 : -1);
        btn.blur();
      });
    });

    qsa('[data-read-more]', home).forEach(function (btn) {
      hydrateReadMoreButton(btn, home);
    });

    viewport.addEventListener('wheel', onWheel, { passive: false });

    if (marqueeEnabled) {
      var railVisible = true;
      if ('IntersectionObserver' in window) {
        var marqueeIo = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            railVisible = entry.isIntersecting;
          });
        }, { rootMargin: '80px 0px' });
        marqueeIo.observe(rail);
      }
      tick = function () {
        if (marqueeEnabled && railVisible && Date.now() >= manualUntil) {
          offsetPx -= autoSpeed;
          normalizeOffset();
        }
        applyTransform();
        animId = requestAnimationFrame(tick);
      };
      tick();
    } else if (prefersReducedMotion) {
      viewport.style.overflowX = 'auto';
    }

    home._homeReviewsRailCleanup = function () {
      if (animId) cancelAnimationFrame(animId);
      window.removeEventListener('resize', syncCardWidth);
    };
  }

  function initSection(section) {
    if (section.getAttribute('data-reviews-init') === '1') return;
    section.setAttribute('data-reviews-init', '1');

    var state = createState(section);
    section._reviewsState = state;

    section.setAttribute('data-star-filter', 'all');
    section.setAttribute('data-country-filter', 'all');

    initInteractions(state);
    initForm(section);
    syncHeroFromReviews(section);

    resetVisibleLimit(state);
    state.previousVisible = 0;
    applyViewState(state);
    animateBars(section);
    initStarAnimations(section);
    hydrateCountryFlags(section);

    section.classList.remove('sap-reviews--loading');
    section.classList.add('is-ready');

    qsa('[data-read-more]', section).forEach(function (btn) {
      hydrateReadMoreButton(btn, section);
    });

    scheduleCatalogPrefetch(state);
  }

  function bindReadMoreDelegation() {
    if (document.documentElement.getAttribute('data-sap-read-more-bound') === '1') return;
    document.documentElement.setAttribute('data-sap-read-more-bound', '1');

    document.addEventListener('click', function (e) {
      var readBtn = e.target.closest('[data-read-more]');
      if (!readBtn) return;

      var section = readBtn.closest('[data-sap-reviews], [data-home-reviews], .sap-home-reviews, .sap-reviews, #home-reviews');
      if (!section) return;

      e.preventDefault();
      e.stopPropagation();
      toggleReviewReadMore(readBtn, section);
    }, true);
  }

  function init() {
    bindReadMoreDelegation();
    refreshStaleHomeReviews();
    qsa('[data-sap-reviews]').forEach(initSection);
    initReviewSubmitPage();
    qsa('[data-home-reviews]').forEach(initHomeReviewsRail);
    hydrateCountryFlags(document.querySelector('.sap-home-reviews'));
  }

  document.addEventListener('shopify:section:unload', function (event) {
    cleanupStarAnimations(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
